import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { Plus, PencilSimple, Check, X, Clock, Trash, Warning, ArrowsClockwise, CheckCircle, CaretDown, CaretUp } from '@phosphor-icons/react';
import AddTorDialog from '@/components/AddTorDialog';
import EditTorDialog from '@/components/EditTorDialog';
import RemoveTorDialog from '@/components/RemoveTorDialog';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = {
  INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3',
  AED: 'AED', SGD: 'S$', AUD: 'A$', JPY: '\u00A5'
};

const CLEARED_STATUSES = ['settling', 'settled'];

function TorItem({ tor, isDark, onEdit, onRespond, onRemove, currentUserId, isCleared }) {
  const symbol = CURRENCY_SYMBOLS[tor.currency] || tor.currency;
  const isPending = tor.status === 'pending';
  const isUnlinked = tor.status === 'unlinked';
  const isAccepted = tor.status === 'accepted';
  const isPendingRemoval = tor.status === 'pending_removal';
  const isSettling = tor.status === 'settling';
  const isSettled = tor.status === 'settled';
  const showRemovalButtons = isPendingRemoval && tor.removal_requested_by && tor.removal_requested_by !== currentUserId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className={`p-3.5 border-b last:border-b-0 ${isDark ? 'border-zinc-800' : 'border-zinc-200'} ${isCleared ? 'opacity-60' : ''}`}
      data-testid={`tor-item-${tor.tor_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`font-ibm text-sm font-medium truncate ${isCleared ? 'line-through' : ''} ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
              {tor.name}
            </span>
            {isUnlinked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-500/10 border border-zinc-500/30 rounded-sm" data-testid={`tor-unlinked-badge-${tor.tor_id}`}>
                <Clock size={10} weight="bold" className={isDark ? 'text-zinc-400' : 'text-zinc-500'} />
                <span className={`font-ibm text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Awaiting Match</span>
              </span>
            )}
            {isPending && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-400/10 border border-yellow-400/30 rounded-sm" data-testid={`tor-pending-badge-${tor.tor_id}`}>
                <Clock size={10} weight="bold" className="text-yellow-400" />
                <span className="font-ibm text-[10px] font-semibold text-yellow-400 uppercase tracking-wider">Pending</span>
              </span>
            )}
            {isAccepted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-sm">
                <Check size={10} weight="bold" className="text-green-500" />
                <span className="font-ibm text-[10px] font-semibold text-green-500 uppercase tracking-wider">Accepted</span>
              </span>
            )}
            {isPendingRemoval && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded-sm">
                <Warning size={10} weight="bold" className="text-red-400" />
                <span className="font-ibm text-[10px] font-semibold text-red-400 uppercase tracking-wider">Removal Pending</span>
              </span>
            )}
            {isSettling && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-400/10 border border-yellow-400/30 rounded-sm">
                <ArrowsClockwise size={10} weight="bold" className="text-yellow-400 animate-spin" style={{ animationDuration: '2s' }} />
                <span className="font-ibm text-[10px] font-semibold text-yellow-400 uppercase tracking-wider">Settling</span>
              </span>
            )}
            {isSettled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-sm">
                <CheckCircle size={10} weight="bold" className="text-green-500" />
                <span className="font-ibm text-[10px] font-semibold text-green-500 uppercase tracking-wider">Cleared</span>
              </span>
            )}
          </div>
          <div className={`font-ibm text-xs space-y-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            {tor.email && <p>{tor.email}</p>}
            {tor.phone && <p>{tor.phone}</p>}
            {tor.since && <p>Since: {new Date(tor.since).toLocaleDateString()}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`font-outfit text-lg font-bold ${isCleared ? 'line-through' : ''} ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
            {symbol}{tor.amount?.toLocaleString()}
          </span>
          {!tor.is_reverse && (isPending || isUnlinked) && (
            <>
              <button
                onClick={() => onEdit(tor)}
                className={`p-1.5 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-500 hover:text-yellow-400' : 'hover:bg-zinc-100 text-zinc-400 hover:text-yellow-500'}`}
                data-testid={`edit-tor-${tor.tor_id}`}
              >
                <PencilSimple size={14} weight="bold" />
              </button>
              <button
                onClick={() => onRemove(tor)}
                className={`p-1.5 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-500 hover:text-red-400' : 'hover:bg-zinc-100 text-zinc-400 hover:text-red-500'}`}
                data-testid={`remove-tor-${tor.tor_id}`}
              >
                <Trash size={14} weight="bold" />
              </button>
            </>
          )}
          {!tor.is_reverse && isAccepted && (
            <span className={`font-ibm text-[9px] uppercase tracking-wider ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`} data-testid={`tor-locked-${tor.tor_id}`} title="Accepted obligations are locked into clearance">Locked</span>
          )}
        </div>
      </div>

      {isPending && tor.is_reverse && (
        <div className="flex gap-2 mt-3">
          <button onClick={() => onRespond(tor.tor_id, 'accept')} className="flex-1 h-8 bg-green-500/10 border border-green-500/30 text-green-500 font-ibm text-xs font-semibold rounded-sm hover:bg-green-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-1" data-testid={`accept-tor-${tor.tor_id}`}>
            <Check size={14} weight="bold" /> Accept
          </button>
          <button onClick={() => onRespond(tor.tor_id, 'reject')} className="flex-1 h-8 bg-red-500/10 border border-red-500/30 text-red-500 font-ibm text-xs font-semibold rounded-sm hover:bg-red-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-1" data-testid={`reject-tor-${tor.tor_id}`}>
            <X size={14} weight="bold" /> Reject
          </button>
        </div>
      )}

      {showRemovalButtons && (
        <div className="flex gap-2 mt-3">
          <button onClick={() => onRespond(tor.tor_id, 'accept_removal')} className="flex-1 h-8 bg-red-500/10 border border-red-500/30 text-red-400 font-ibm text-xs font-semibold rounded-sm hover:bg-red-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-1" data-testid={`accept-removal-${tor.tor_id}`}>Accept Removal</button>
          <button onClick={() => onRespond(tor.tor_id, 'reject_removal')} className="flex-1 h-8 bg-green-500/10 border border-green-500/30 text-green-500 font-ibm text-xs font-semibold rounded-sm hover:bg-green-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-1" data-testid={`reject-removal-${tor.tor_id}`}>Keep</button>
        </div>
      )}
    </motion.div>
  );
}

function TorList({ title, subtitle, type, tors, isDark, onAdd, onEdit, onRespond, onRemove, currentUserId }) {
  const [showCleared, setShowCleared] = useState(false);

  const activeTors = tors.filter(t => !CLEARED_STATUSES.includes(t.status));
  const clearedTors = tors.filter(t => CLEARED_STATUSES.includes(t.status));
  const needsScroll = activeTors.length > 5;

  return (
    <div className={`border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid={`${type}s-section`}>
      {/* Header */}
      <div className={`p-4 flex items-center justify-between ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
        <div>
          <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{title}</h3>
          <p className={`font-ibm text-[10px] uppercase tracking-wider mt-0.5 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTors.length > 0 && (
            <span className={`font-ibm text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-200 text-zinc-600'}`}>
              {activeTors.length}
            </span>
          )}
          <button
            onClick={onAdd}
            className="w-8 h-8 bg-yellow-400 text-zinc-950 rounded-sm flex items-center justify-center hover:bg-yellow-500 active:scale-[0.95] transition-all"
            data-testid={`add-${type}-button`}
          >
            <Plus size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Active Tor List (scrollable when >5) */}
      <div
        className={`${isDark ? 'bg-zinc-950' : 'bg-white'} ${needsScroll ? 'max-h-[340px] overflow-y-auto' : ''}`}
        style={needsScroll ? { scrollbarWidth: 'thin', scrollbarColor: isDark ? '#3f3f46 #18181b' : '#d4d4d8 #ffffff' } : {}}
        data-testid={`${type}s-active-list`}
      >
        <AnimatePresence>
          {activeTors.length === 0 ? (
            <div className={`p-8 text-center font-ibm text-sm ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
              No {type}s yet
            </div>
          ) : (
            activeTors.map(tor => (
              <TorItem key={tor.tor_id} tor={tor} isDark={isDark} onEdit={onEdit} onRespond={onRespond} onRemove={onRemove} currentUserId={currentUserId} isCleared={false} />
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Cleared Toggle — always visible */}
      <button
        onClick={() => setShowCleared(!showCleared)}
        className={`w-full flex items-center justify-between px-4 py-2.5 border-t transition-colors ${isDark ? 'border-zinc-800 hover:bg-zinc-900/50' : 'border-zinc-200 hover:bg-zinc-50'}`}
        data-testid={`toggle-cleared-${type}s`}
      >
        <div className="flex items-center gap-2">
          <CheckCircle size={14} weight="bold" className="text-green-500" />
          <span className={`font-ibm text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Cleared{clearedTors.length > 0 ? ` (${clearedTors.length})` : ''}
          </span>
        </div>
        {showCleared
          ? <CaretUp size={14} className={isDark ? 'text-zinc-500' : 'text-zinc-400'} />
          : <CaretDown size={14} className={isDark ? 'text-zinc-500' : 'text-zinc-400'} />
        }
      </button>

      <AnimatePresence>
        {showCleared && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`border-t ${isDark ? 'border-zinc-800 bg-zinc-950/50' : 'border-zinc-200 bg-zinc-50/50'}`}>
              {clearedTors.length === 0 ? (
                <div className={`p-6 text-center ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                  <CheckCircle size={24} weight="thin" className="mx-auto mb-2 opacity-40" />
                  <p className="font-ibm text-xs">Nothing settled yet.</p>
                  <p className="font-ibm text-[10px] mt-0.5 opacity-70">Once settled, cleared entries will appear here.</p>
                </div>
              ) : (
                clearedTors.map(tor => (
                  <TorItem key={tor.tor_id} tor={tor} isDark={isDark} onEdit={onEdit} onRespond={onRespond} onRemove={onRemove} currentUserId={currentUserId} isCleared={true} />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TorsArea({ tors, currency, onRefresh }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme === 'dark';
  const currentUserId = user?.user_id;

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addType, setAddType] = useState('debtor');
  const [editTor, setEditTor] = useState(null);
  const [removeTor, setRemoveTor] = useState(null);

  const debtors = tors.filter(t => t.tor_type === 'debtor');
  const creditors = tors.filter(t => t.tor_type === 'creditor');

  const activeDebtors = debtors.filter(t => !CLEARED_STATUSES.includes(t.status));
  const activeCreditors = creditors.filter(t => !CLEARED_STATUSES.includes(t.status));

  const handleAddClick = (type) => {
    setAddType(type);
    setAddDialogOpen(true);
  };

  const handleRespond = async (torId, action) => {
    try {
      await axios.post(`${API}/tors/${torId}/respond`, { action }, { withCredentials: true });
      toast.success(`Request ${action}ed`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to respond');
    }
  };

  // Summary uses ACTIVE tors only (cleared ones are done)
  const totalOwed = activeDebtors.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalOwing = activeCreditors.reduce((sum, t) => sum + (t.amount || 0), 0);
  const symbol = CURRENCY_SYMBOLS[currency] || currency;

  return (
    <div data-testid="tors-area">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="total-owed-card">
          <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Total Owed to You
          </p>
          <p className="font-outfit text-2xl font-bold text-green-500">{symbol}{totalOwed.toLocaleString()}</p>
        </div>
        <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="total-owing-card">
          <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Total You Owe
          </p>
          <p className="font-outfit text-2xl font-bold text-red-400">{symbol}{totalOwing.toLocaleString()}</p>
        </div>
      </div>

      {/* Debtors & Creditors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TorList
          title="Debtors"
          subtitle="Person who owes money to you"
          type="debtor"
          tors={debtors}
          isDark={isDark}
          onAdd={() => handleAddClick('debtor')}
          onEdit={setEditTor}
          onRespond={handleRespond}
          onRemove={setRemoveTor}
          currentUserId={currentUserId}
        />
        <TorList
          title="Creditors"
          subtitle="Person you owe money to"
          type="creditor"
          tors={creditors}
          isDark={isDark}
          onAdd={() => handleAddClick('creditor')}
          onEdit={setEditTor}
          onRespond={handleRespond}
          onRemove={setRemoveTor}
          currentUserId={currentUserId}
        />
      </div>

      <AddTorDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        type={addType}
        currency={currency}
        onSuccess={onRefresh}
      />
      {editTor && (
        <EditTorDialog open={!!editTor} onClose={() => setEditTor(null)} tor={editTor} onSuccess={onRefresh} />
      )}
      {removeTor && (
        <RemoveTorDialog open={!!removeTor} onClose={() => setRemoveTor(null)} tor={removeTor} onSuccess={onRefresh} />
      )}
    </div>
  );
}
